import {
  DespatchAdviceItem,
  InventoryChangeReason,
  Unit,
  CommonInboundingType,
} from "__graphql__/types";
import { isEmptyObject, isNullOrUndefined } from "utils/tsHelpers";

import {
  DeliveryType,
  DespatchSummaryItem,
  InboundUnitStockUpdate,
  SelectedProduct,
} from "./types";

interface StockUpdatePlan {
  reason: InventoryChangeReason;
}

export function sumStockUpdatePlanTotal(
  stockUpdatePlan: InboundUnitStockUpdate["stockUpdatePlan"],
) {
  return stockUpdatePlan.reduce((sum, plan) => plan.quantityDelta + sum, 0);
}

export function extractInboundPlan<T extends StockUpdatePlan>(
  stockUpdatePlan: T[],
  inventoryChangeReason?: InventoryChangeReason,
): T | undefined {
  const defaultReason = InventoryChangeReason.inbound_goods_received;
  return stockUpdatePlan.find(({ reason }) => reason === inventoryChangeReason || defaultReason);
}

const hasSameHuSize = (items: DespatchAdviceItem[]): boolean => {
  const uniqueSizes = new Set(items.map((item) => item.handlingUnitSize));
  return uniqueSizes.size === 1;
};

const getHuUnitSize = (items: DespatchAdviceItem[]) => {
  return items.length === 1 || hasSameHuSize(items) ? items[0].handlingUnitSize : 1;
};

export function getDisplayUnitSize(
  quantity: number | null | undefined,
  handlingUnitSize: number | null | undefined,
): number {
  if (!quantity || quantity <= 0) return 0;

  const size = handlingUnitSize && handlingUnitSize > 1 ? quantity / handlingUnitSize : quantity;

  return Math.floor(size);
}

const prefill = (items: DespatchAdviceItem[]): DespatchSummaryItem | null => {
  const totalQuantity = items.reduce((sum, item) => sum + item.totalQuantity, 0);

  if (totalQuantity <= 0) return null;

  const handlingUnitSize = getHuUnitSize(items);
  const remainder = totalQuantity % handlingUnitSize;
  const newTotalValue = totalQuantity - remainder;

  if (newTotalValue <= 0) {
    return {
      unitType: "single",
      handlingUnitSize: 1,
      unitSizeForDisplay: 1,
      totalQuantity: remainder,
    };
  }
  return {
    unitType: handlingUnitSize === 1 ? "single" : "handling",
    handlingUnitSize,
    unitSizeForDisplay: getDisplayUnitSize(totalQuantity, handlingUnitSize),
    totalQuantity,
  };
};

export const filteredDespatchAdviceBySku = (
  sku: string,
  despatchAdviceItems: Record<string, DespatchAdviceItem[]>,
) => {
  if (isEmptyObject(despatchAdviceItems)) return null;

  const matchingItems = Object.values(despatchAdviceItems)
    .flat()
    .filter((item: DespatchAdviceItem) => Array.isArray(item?.skus) && item.skus.includes(sku));

  if (!matchingItems?.length) return null;

  const isTotalQuantityEstimated = matchingItems.some(
    (item: DespatchAdviceItem) => item.isTotalQuantityEstimate,
  );

  if (isTotalQuantityEstimated) return null;

  return matchingItems;
};

export const selectDespatchAdviceItemBySku = (
  sku: string,
  despatchAdviceItems: Record<string, DespatchAdviceItem[]>,
) => {
  if (isEmptyObject(despatchAdviceItems)) return null;

  const matchingItems = filteredDespatchAdviceBySku(sku, despatchAdviceItems);

  if (isNullOrUndefined(matchingItems)) return null;

  return prefill(matchingItems);
};

export const getMatchedDesadvId = (
  sku: string,
  despatchAdviceItems: Record<string, DespatchAdviceItem[]>,
) => {
  if (isEmptyObject(despatchAdviceItems)) return null;

  const matchingKey = Object.entries(despatchAdviceItems).find(
    ([, items]) =>
      Array.isArray(items) &&
      items.some((item) => Array.isArray(item?.skus) && item.skus.includes(sku)),
  );
  return matchingKey ? matchingKey[0] : null;
};

export const getUniqueHandlingUnitSizes = (
  units?: {
    quantity: Unit["quantity"];
  }[],
): number[] => {
  const handlingUnitSizes = units
    ? units.filter((unit: any) => unit.quantity > 1).map((unit: any) => unit.quantity)
    : [];

  return Array.from(new Set(handlingUnitSizes));
};

export const createInitialStockUpdate = (
  reason: InventoryChangeReason,
  initialQuantity: number,
  stockUpdated?: boolean,
): InboundUnitStockUpdate => ({
  stockUpdatePlan: [
    {
      reason,
      quantityDelta: initialQuantity,
    },
  ],
  stockUpdated: stockUpdated ?? false,
});

const DeliveryTypeMapping = {
  [DeliveryType.REWE]: CommonInboundingType.inbounding_type_rewe,
  [DeliveryType.Lekkerland]: CommonInboundingType.inbounding_type_lekkerland,
  [DeliveryType.OtherDelivery]: CommonInboundingType.inbounding_type_other_delivery,
  [DeliveryType.NotDelivery]: CommonInboundingType.inbounding_type_not_delivery,
} as const;

const ReverseDeliveryTypeMapping = Object.fromEntries(
  Object.entries(DeliveryTypeMapping).map(([key, value]) => [value, key]),
) as Record<CommonInboundingType, DeliveryType>;

export const mapDeliveryTypeToInboundingType = (
  deliveryType: DeliveryType | null,
): CommonInboundingType | null => {
  return deliveryType !== null && deliveryType in DeliveryTypeMapping
    ? DeliveryTypeMapping[deliveryType]
    : null;
};

export const mapInboundingTypeToDeliveryType = (
  inboundingType: CommonInboundingType | null,
): DeliveryType | null => {
  return inboundingType !== null && inboundingType in ReverseDeliveryTypeMapping
    ? ReverseDeliveryTypeMapping[inboundingType]
    : null;
};

type DroppingProductInput =
  SelectedProduct | { productSku: string; quantity?: number; handlingUnitSize?: number };

export const buildProductPayload = (
  product: DroppingProductInput,
  despatchAdviceItems: Record<string, DespatchAdviceItem[]>,
  deliverySSCC: string | null,
) => {
  const sku = "sku" in product ? product.sku : product.productSku;
  const quantity = product.quantity ?? 1;
  const handlingUnitSize = product.handlingUnitSize ?? 1;

  const despatchAdviceItem = selectDespatchAdviceItemBySku(sku, despatchAdviceItems);
  const desadv = getMatchedDesadvId(sku, despatchAdviceItems);

  return {
    sku,
    quantity: despatchAdviceItem?.totalQuantity ?? quantity,
    handlingUnitSize: despatchAdviceItem?.handlingUnitSize ?? handlingUnitSize,
    metadata: {
      desadv,
      expectedQuantity: despatchAdviceItem?.totalQuantity?.toString(),
      sscc: deliverySSCC,
    },
  };
};
